import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar as CalendarIcon, Clock, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';

const BookingModal = ({ isOpen, onClose, services }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    date: null,
    time: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const timeSlots = [
    '09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM', '06:00 PM'
  ];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleDateSelect = (date) => {
    setFormData({ ...formData, date });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const bookingData = {
        ...formData,
        date: formData.date ? formData.date.toISOString().split('T')[0] : ''
      };
      await axios.post(`${API}/bookings`, bookingData);
      toast.success('Booking request submitted! We\'ll contact you within 24 hours.');
      onClose();
      setStep(1);
      setFormData({ name: '', email: '', phone: '', service: '', date: null, time: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStepValid = () => {
    switch (step) {
      case 1:
        return formData.name && formData.email && formData.phone;
      case 2:
        return formData.service;
      case 3:
        return formData.date && formData.time;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (isStepValid()) {
      if (step === 3) {
        handleSubmit();
      } else {
        setStep(step + 1);
      }
    }
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-[#0a0a0a]/90 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="booking-modal-overlay"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-[#121212] border border-[#262626] w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          data-testid="booking-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-[#262626]">
            <div>
              <h2 className="font-['Playfair_Display'] text-2xl text-[#e5e5e5]">
                Book a Session
              </h2>
              <p className="text-[#a3a3a3] text-sm mt-1">Step {step} of 3</p>
            </div>
            <button
              onClick={onClose}
              className="text-[#a3a3a3] hover:text-[#e5e5e5] transition-colors"
              data-testid="booking-modal-close"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="px-6 py-4 border-b border-[#262626]">
            <div className="flex gap-2">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`h-1 flex-1 transition-colors ${
                    s <= step ? 'bg-[#d4af37]' : 'bg-[#262626]'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <AnimatePresence mode="wait">
              {/* Step 1: Personal Details */}
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <h3 className="font-['Playfair_Display'] text-xl text-[#e5e5e5] mb-6">
                    Your Details
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="text-[#a3a3a3] text-sm tracking-wider uppercase block mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full bg-[#1a1a1a] border border-[#262626] py-3 px-4 text-[#e5e5e5] focus:border-[#d4af37] focus:outline-none transition-colors"
                        placeholder="Enter your full name"
                        data-testid="booking-name-input"
                      />
                    </div>
                    <div>
                      <label className="text-[#a3a3a3] text-sm tracking-wider uppercase block mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full bg-[#1a1a1a] border border-[#262626] py-3 px-4 text-[#e5e5e5] focus:border-[#d4af37] focus:outline-none transition-colors"
                        placeholder="Enter your email"
                        data-testid="booking-email-input"
                      />
                    </div>
                    <div>
                      <label className="text-[#a3a3a3] text-sm tracking-wider uppercase block mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full bg-[#1a1a1a] border border-[#262626] py-3 px-4 text-[#e5e5e5] focus:border-[#d4af37] focus:outline-none transition-colors"
                        placeholder="Enter your phone number"
                        data-testid="booking-phone-input"
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Step 2: Service Selection */}
              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <h3 className="font-['Playfair_Display'] text-xl text-[#e5e5e5] mb-6">
                    Select Service
                  </h3>
                  <div className="grid gap-4">
                    {services && services.map((service) => (
                      <button
                        key={service.id}
                        onClick={() => setFormData({ ...formData, service: service.title })}
                        className={`p-4 border text-left transition-all ${
                          formData.service === service.title
                            ? 'border-[#d4af37] bg-[#d4af37]/10'
                            : 'border-[#262626] bg-[#1a1a1a] hover:border-[#d4af37]/50'
                        }`}
                        data-testid={`booking-service-${service.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-[#e5e5e5] font-medium">{service.title}</p>
                            <p className="text-[#a3a3a3] text-sm mt-1">Starting at {service.starting_price}</p>
                          </div>
                          {formData.service === service.title && (
                            <Check className="w-5 h-5 text-[#d4af37]" />
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Step 3: Date & Time */}
              {step === 3 && (
                <motion.div
                  key="step3"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  <h3 className="font-['Playfair_Display'] text-xl text-[#e5e5e5] mb-6">
                    Choose Date & Time
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Calendar */}
                    <div className="bg-[#1a1a1a] p-4 border border-[#262626]">
                      <div className="flex items-center gap-2 mb-4">
                        <CalendarIcon className="w-5 h-5 text-[#d4af37]" />
                        <span className="text-[#a3a3a3] text-sm tracking-wider uppercase">Select Date</span>
                      </div>
                      <Calendar
                        mode="single"
                        selected={formData.date}
                        onSelect={handleDateSelect}
                        disabled={(date) => date < new Date()}
                        className="rounded-none border-0"
                        data-testid="booking-calendar"
                      />
                    </div>

                    {/* Time Slots */}
                    <div className="bg-[#1a1a1a] p-4 border border-[#262626]">
                      <div className="flex items-center gap-2 mb-4">
                        <Clock className="w-5 h-5 text-[#d4af37]" />
                        <span className="text-[#a3a3a3] text-sm tracking-wider uppercase">Select Time</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        {timeSlots.map((time) => (
                          <button
                            key={time}
                            onClick={() => setFormData({ ...formData, time })}
                            className={`py-2 px-3 text-sm transition-all ${
                              formData.time === time
                                ? 'bg-[#d4af37] text-[#0a0a0a]'
                                : 'bg-[#262626] text-[#a3a3a3] hover:bg-[#d4af37]/20 hover:text-[#d4af37]'
                            }`}
                            data-testid={`booking-time-${time.replace(/[:\s]/g, '-')}`}
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Additional Message */}
                  <div>
                    <label className="text-[#a3a3a3] text-sm tracking-wider uppercase block mb-2">
                      Additional Notes (Optional)
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={3}
                      className="w-full bg-[#1a1a1a] border border-[#262626] py-3 px-4 text-[#e5e5e5] focus:border-[#d4af37] focus:outline-none transition-colors resize-none"
                      placeholder="Any special requests or requirements..."
                      data-testid="booking-message-input"
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-[#262626]">
            <Button
              onClick={prevStep}
              variant="outline"
              disabled={step === 1}
              className="border-[#262626] text-[#a3a3a3] hover:bg-[#1a1a1a] hover:text-[#e5e5e5] rounded-none px-6 disabled:opacity-50"
              data-testid="booking-prev-btn"
            >
              Back
            </Button>
            <Button
              onClick={nextStep}
              disabled={!isStepValid() || isSubmitting}
              className="bg-[#d4af37] text-[#0a0a0a] hover:bg-[#b3922d] rounded-none px-8 disabled:opacity-50"
              data-testid="booking-next-btn"
            >
              {isSubmitting ? 'Submitting...' : step === 3 ? 'Submit Booking' : 'Continue'}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BookingModal;
